//@flow
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import prefix from 'react-prefixer'
import get from 'lodash.get'
import set from 'lodash.set'
import merge from 'lodash.merge'
import cloneDeep from 'lodash.clonedeep'
import seededshuffle from 'seededshuffle'
import Elements from './QuestionElements'

// Calculated each time this file loads to ensure stable shuffles between renders
const shuffleSeed = Math.floor(Math.random() * 1000)

/**
 * Renders elements based on provided elements/config.
 * Allows for dynamic serialised storage of question display elements.
 * @memberof components
 */
class ElementRenderer extends Component {
  static contextTypes = {
    onQuestionStateChange: PropTypes.func,
  }
  static childContextTypes = {
    onChange: PropTypes.func,
  }
  getChildContext() {
    return {
      onChange: this.handleChange,
    }
  }
  processChild(child: string | Object, props: Object) {
    return typeof child === 'string'
      ? child
      : <ElementRenderer {...child} {...props} />
  }
  getState(state: RawLearnerAnswer, key: string) {
    if (key == null) return state
    return get(state, key)
  }
  applyState(
    state: RawLearnerAnswer,
    key: string,
    value: RawLearnerAnswer,
  ): RawLearnerAnswer {
    if (key == null) return value
    return set(cloneDeep(state), key, value)
  }
  handleChange = (stateKey: string, newState: RawLearnerAnswer) =>
    this.context.onQuestionStateChange(
      this.applyState(this.props.questionState, stateKey, newState),
    )
  processProps = (
    restProps: Object,
    processElements: Array<string>,
    processStyles: Array<string>,
    shuffle: Array<string>,
  ) => {
    const props = {}
    for (const key of processElements) {
      const toProcess = get(restProps, key)
      if (toProcess) {
        set(
          props,
          key,
          Array.isArray(toProcess)
            ? toProcess.map((child, i) =>
                this.processChild(child, {
                  questionState: this.props.questionState,
                  key: i,
                }),
              )
            : this.processChild(toProcess, {
                questionState: this.props.questionState,
              }),
        )
      }
    }
    for (const key of shuffle) {
      const prop = get(props, key) || get(restProps, key)
      if (prop) {
        set(props, key, seededshuffle.shuffle(prop, shuffleSeed))
      }
    }
    for (const key of processStyles) {
      const prop = get(props, key) || get(restProps, key)
      if (prop) {
        set(props, key, prefix(prop))
      }
    }
    return props
  }
  render() {
    const {
      type,
      stateKey,
      questionState,
      shuffle = [],
      processElements = [],
      processStyles = [],
      ...restProps
    } = this.props
    const Component = Elements[type] || type
    const processedProps = this.processProps(
      restProps,
      [...processElements, 'children'],
      [...processStyles, 'style'],
      shuffle,
    )
    const additionalElementProps = merge({}, restProps, processedProps)
    if (Elements[type]) {
      // custom question component
      return (
        <Component
          questionState={this.getState(questionState, stateKey)}
          stateKey={stateKey}
          {...additionalElementProps}
        />
      )
    } else {
      // standard component
      return <Component {...additionalElementProps} />
    }
  }
}

export default ElementRenderer



// WEBPACK FOOTER //
// ./src/components/ElementRenderer.js